import React, { useState } from 'react';
import { DB, uploadImage, canCustomerOffer } from '../data/database';
import { ROLES, CUT_TYPES, exportCSV, CUTTABLE_CATEGORIES, getYardLocations, getDestinationCountries, getAllocatedZoneCodes } from '../data/constants';
import { getPartType, normalizeCutType, resolvePartTypeEntry } from '../data/partTypes';
import { formatPrice } from '../utils/currency';
import generateBrandedQR from '../utils/generateBrandedQR';
import Pagination from './ui/Pagination';

// Auto-generate branded QR code for a unit (fire-and-forget)
function autoGenerateQR(sku, dbKey, itemId) {
  generateBrandedQR(`${window.location.origin}/?u=${sku}`, { size: 472 }).then(async (dataUrl) => {
    const url = await uploadImage(dataUrl);
    if (url) {
      const fresh = DB.get(dbKey) || [];
      const result = await DB.setAsync(dbKey, fresh.map(u => u.id === itemId ? { ...u, qr_code_url: url } : u));
      if (result?.error) console.error('[autoGenerateQR] Failed to save QR:', result.error);
    }
  }).catch(() => {});
}

const CUTTABLE_CATS = new Set(CUTTABLE_CATEGORIES);

// Shared PDF style (responsive for all devices)
// Print via hidden iframe — user stays on current page
function printHTML(html) {
  let iframe = document.getElementById('print-frame');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'print-frame';
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
    document.body.appendChild(iframe);
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  iframe.onload = () => {
    try { iframe.contentWindow.print(); } catch {}
  };
}

const PDF_STYLE = `
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;padding:20px;color:#111;font-size:13px;max-width:100%;overflow-x:hidden}
    h1{font-size:18px;margin-bottom:4px}
    .sub{color:#666;font-size:11px;margin-bottom:16px}
    table{width:100%;border-collapse:collapse;table-layout:fixed;word-wrap:break-word}
    th{background:#1a3a6b;color:#fff;text-align:left;padding:7px 8px;font-size:10px;font-weight:600}
    td{padding:6px 8px;border-bottom:1px solid #e0e0e0;font-size:12px;vertical-align:top}
    tr:nth-child(even) td{background:#f6f8fb}
    img{max-width:100%;height:auto;display:block;border-radius:8px;margin-bottom:14px}
    @media print{body{padding:10px}img{max-height:250px;object-fit:contain}}
    @media(max-width:480px){th,td{padding:5px 6px;font-size:10px}h1{font-size:16px}}
  </style>`;

// Multi-unit stock PDF
function exportStockPDF(items, title) {
  if (!items.length) { alert('No data to export.'); return; }
  const rows = items.map(i => `
    <tr>
      <td>${i.sku || ''}</td>
      <td>${i.chassis || ''}</td>
      <td>${i.make ? `${i.make} ${i.model}` : i.engineName || i.partName || ''}</td>
      <td style="text-transform:capitalize">${(i.status || '').replace(/_/g, ' ')}</td>
      <td>${i.year || ''}</td>
      <td>${[i.city, i.country].filter(Boolean).join(', ') || i.location || i.port || ''}</td>
      <td>${i.daysInStock ?? ''}</td>
    </tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${PDF_STYLE}<title>${title}</title></head><body>
    <h1>BIG STAR — ${title}</h1>
    <div class="sub">Generated ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})} · ${items.length} units</div>
    <table><tr><th>SKU</th><th>Chassis</th><th>Name</th><th>Status</th><th>Year</th><th>Location</th><th>Days</th></tr>${rows}</table>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`;
  printHTML(html);
}

// Single-unit detail PDF
function downloadUnitPDF(item) {
  const name = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || 'Unit';
  const extraRows = item.extraSpecs ? Object.entries(item.extraSpecs)
    .filter(([, v]) => v !== '' && v !== false && v !== null && v !== undefined)
    .map(([k, v]) => [k.replace(/_/g, ' '), typeof v === 'boolean' ? 'Yes' : String(v).replace(/_/g, ' ')]) : [];
  const rows = [
    ['SKU', item.sku], ['Status', (item.status || '').replace('_', ' ')],
    ['Vehicle Category', item.vehicleCategory && item.vehicleCategory !== 'passenger_car' ? item.vehicleCategory.replace(/_/g, ' ') : ''],
    ['Year', item.year], ['Chassis', item.chassis], ['Make', item.make], ['Model', item.model],
    ['Engine', item.engineCode], ['Displacement', item.displacement],
    ['Mileage', item.mileage ? `${item.mileage.toLocaleString()} km` : ''],
    ['Score', item.score], ['Grade', item.grade],
    ['Color', item.color], ['Transmission', item.transmission],
    ['Drive System', item.driveSystem], ['Steering', item.steer], ['Seats', item.seats],
    ['Country', item.country], ['City', item.city], ['Location', item.location],
    ['Engine Name', item.engineName], ['Manufacturer', item.manufacturer],
    ['Part Name', item.partName], ['Category', item.category], ['Condition', item.condition],
    ['Fitment', item.fitment], ['Quantity', item.quantity],
    ...extraRows,
    ['Days in Stock', item.daysInStock], ['Added', item.createdAt],
  ].filter(([, v]) => v);
  const imgTag = item.images?.[0] ? `<img src="${item.images[0]}" style="max-height:280px;object-fit:contain">` : '';
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${PDF_STYLE}<title>${name}</title>
    <style>td:first-child{font-weight:600;color:#555;width:120px}</style></head><body>
    <h1>${name}</h1>${imgTag}
    <table>${rows.map(([k, v]) => `<tr><td>${k}</td><td>${v}</td></tr>`).join('')}</table>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`;
  printHTML(html);
}
import { EditIcon } from './Icons';
import { useLanguage } from '../translations/LanguageContext';
import { CarIcon, EngineIcon, PartsIcon, PlusIcon, PackageIcon, SearchIcon, GridIcon, ListIcon, CheckIcon, DeleteIcon } from './Icons';
import DetailModal from './modals/DetailModal';
import AddModal from './modals/AddModal';
import AddUnitChooser from './modals/AddUnitChooser';
import PartsRequestModal from './modals/PartsRequestModal';
import { logActivity } from '../data/notifications';

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest First' },
  { value: 'oldest',     label: 'Oldest First' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'price_asc',  label: 'Price: Low → High' },
  { value: 'days_desc',  label: 'Days in Stock ↓' },
];

const EMPTY_FILTERS = {
  status: '', make: '', year: '', transmission: '',
  condition: '', category: '', sort: 'newest', location: '', yard: '', supplier: '',
  zone: '', destCountry: ''
};

const STATUS_LABEL = {
  japan_yard: 'Japan Yard',
  in_transit:  'In Transit',
  available:   'Available',
  assigned:    'Assigned',
  sold:        'Sold',
};

const STATUS_CLASS = {
  japan_yard: 'pending',
  in_transit:  'transit',
  available:   'available',
  assigned:    'assigned',
  sold:        'sold',
};

// Cut type display metadata
const CUT_TYPE_META = {
  'half-cut': { icon: '✂️', desc: 'Upper half section' },
  'nose-cut': { icon: '🔧', desc: 'Front section with engine bay' },
  'ckd':      { icon: '📦', desc: 'Complete knock-down' },
};

function Inventory({ user, currency, lang }) {
  const { t } = useLanguage();
  const [tab, setTab]               = useState('vehicles');
  const [detail, setDetail]         = useState(null);
  const [showAdd, setShowAdd]       = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  const [, forceUpdate]             = useState();
  const [viewMode, setViewMode]     = useState('grid');
  const [search, setSearch]         = useState('');
  const [filters, setFilters]       = useState(EMPTY_FILTERS);
  // Customer-specific state
  const [cutTypeModal, setCutTypeModal]   = useState(null);
  const [customCutNote, setCustomCutNote] = useState('');
  const [selectedCut, setSelectedCut]     = useState(null);
  const [editItem, setEditItem]           = useState(null);
  const [partsReqVehicle, setPartsReqVehicle] = useState(null);
  const [dlOpen, setDlOpen]               = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [pageNum, setPageNum]             = useState(1);
  const [perPage, setPerPage]             = useState(25);
  const [selectMode, setSelectMode]       = useState(false);
  const [selected, setSelected]           = useState(new Set());

  const role = ROLES[user.role] || {};
  const isYardSales = user.role === 'yard_sales';
  const customers = DB.get('customers') || [];

  const vehicles = DB.get('vehicles')   || [];
  const engines  = DB.get('engines')    || [];
  const parts    = DB.get('spareParts') || [];

  // Yard roles: operator sees own uploads, manager sees their yard's units
  // Customers: hide sold units unless assigned to them
  const isYardRole = user.role === 'yard_operator' || user.role === 'yard_manager';
  const isManager  = user.role === 'yard_manager';
  const yardFilter = (items) => {
    if (!isYardRole) return items;
    return isManager
      ? items.filter(i => i.destination_yard === user.yard)
      : items.filter(i => i.created_by === user.id);
  };
  const baseVehicles = yardFilter(role.isCustomer
    ? vehicles.filter(v => v.status !== 'sold' || v.assignedTo === user.id)
    : vehicles.filter(v => v.status !== 'converted' || filters.status === 'converted'));
  const baseEngines = yardFilter(role.isCustomer
    ? engines.filter(e => e.status !== 'sold' || e.assignedTo === user.id)
    : engines);
  const baseParts = yardFilter(role.isCustomer
    ? parts.filter(p => p.status !== 'sold' || p.assignedTo === user.id)
    : parts);

  // When searching, search across ALL inventory. Otherwise show current tab only.
  const allCombined = [
    ...baseVehicles.map(v => ({ ...v, _tab: 'vehicles' })),
    ...baseEngines.map(e => ({ ...e, _tab: 'engines' })),
    ...baseParts.map(p => ({ ...p, _tab: 'parts' })),
  ];
  const allItems = (search.trim() || filters.yard || filters.location)
    ? allCombined
    : (tab === 'vehicles' ? baseVehicles : tab === 'engines' ? baseEngines : baseParts);

  // Customer mapping: customerId → { name, country }
  const customerMap = {};
  customers.forEach(c => { customerMap[c.id] = { name: c.name, country: c.country }; });
  // Build customer + country list for filter (only customers that have assigned units)
  const assignedCustomerIds = [...new Set(allItems.map(i => i.assignedTo).filter(Boolean))];
  const customerOptions = assignedCustomerIds
    .map(id => customerMap[id])
    .filter(Boolean)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Unique values for filter dropdowns
  const makes         = [...new Set(baseVehicles.map(v => (v.make || '').trim()).filter(Boolean))].sort();
  const years         = [...new Set(allItems.map(i => i.year).filter(Boolean))].sort((a, b) => b - a);
  const transmissions = [...new Set(baseVehicles.map(v => v.transmission).filter(Boolean))].sort();
  const conditions    = [...new Set([...baseEngines, ...baseParts].map(i => i.condition).filter(Boolean))].sort();
  // Categories from actual inventory — resolve generic "Cut Types"/"Cut Unit" to specific names
  const rawCats = baseParts.map(p => {
    if (p.category === 'Cut Types' || p.category === 'Cut Unit') {
      return CUT_TYPES.find(c => c.id === p.cutType)?.name || p.partName?.split('—').pop()?.trim() || p.category;
    }
    return p.category;
  }).filter(Boolean);
  const categories = [...new Set(rawCats)].sort();

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const hasActiveFilters = search || countryFilter || Object.entries(filters).some(([k, v]) => k !== 'sort' && v !== '');

  const filteredItems = allItems
    .filter(item => {
      const q = search.toLowerCase();
      if (q) {
        const searchFields = [
          item.make, item.model, item.engineName, item.partName,
          item.sku, item.chassis, item.year?.toString(),
          item.make ? `${item.make} ${item.model}` : '',
          item.color, item.category, item.country, item.city,
          item.transmission, item.fuel, item.condition,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchFields.includes(q)) return false;
      }
      // Special customer filter "my_stock"
      if (filters.status === 'my_stock') return item.assignedTo === user.id;
      if (filters.status && item.status !== filters.status) return false;
      // Location filter: where the unit physically is (country)
      if (filters.location) {
        if ((item.country || '') !== filters.location) return false;
      }
      // Destination Yard filter: where the unit is going
      if (filters.yard) {
        if ((item.destination_yard || '') !== filters.yard) return false;
      }
      if (filters.supplier && (item.purchase_supplier || '') !== filters.supplier) return false;
      if (filters.zone && (item.current_zone || '') !== filters.zone) return false;
      if (filters.destCountry && (item.destination_country || '') !== filters.destCountry) return false;
      if (filters.make && (item.make || '').trim() !== filters.make) return false;
      if (filters.year && String(item.year) !== String(filters.year)) return false;
      if (filters.transmission && item.transmission !== filters.transmission) return false;
      if (filters.condition && item.condition !== filters.condition) return false;
      if (filters.category) {
        const itemCat = (item.category === 'Cut Types' || item.category === 'Cut Unit')
          ? (CUT_TYPES.find(c => c.id === item.cutType)?.name || item.partName?.split('—').pop()?.trim() || item.category)
          : item.category;
        if (itemCat !== filters.category) return false;
      }
      // Customer filter: match by customer ID
      if (countryFilter) {
        if (item.assignedTo !== countryFilter) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (filters.sort) {
        case 'oldest':     return (a.createdAt || '').localeCompare(b.createdAt || '');
        case 'price_desc': return (b.priceJPY || 0) - (a.priceJPY || 0);
        case 'price_asc':  return (a.priceJPY || 0) - (b.priceJPY || 0);
        case 'days_desc':  return (b.daysInStock || 0) - (a.daysInStock || 0);
        default:           return (b.createdAt || '').localeCompare(a.createdAt || '');
      }
    });

  // --- Staff handlers ---
  const handleUpdate = async (updatedItem) => {
    const detailTab = detail.type;
    const key = detailTab === 'vehicles' ? 'vehicles' : detailTab === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    await DB.setAsync(key, list.map(i => i.id === updatedItem.id ? updatedItem : i));
    setDetail(prev => ({ ...prev, item: updatedItem }));
    forceUpdate({});
  };

  const handleDelete = (item, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${getItemName(item)}? This cannot be undone.`)) return;
    const key = (item._tab === 'vehicles') ? 'vehicles'
              : (item._tab === 'engines') ? 'engines'
              : (item._tab === 'parts') ? 'spareParts'
              : (tab === 'vehicles' ? 'vehicles' : tab === 'engines' ? 'engines' : 'spareParts');
    const list = DB.get(key) || [];
    DB.set(key, list.filter(i => i.id !== item.id));
    DB.deleteRecord(key, item.id);
    if (item._supaId) DB.deleteRecord(key, item._supaId);
    forceUpdate({});
  };

  const handleSave = async (data) => {
    const addType = showAdd === 'parts' ? 'parts' : tab;
    const isCutVehicle = addType === 'vehicles' && !!data.cutType;
    const isPart = addType === 'parts' || isCutVehicle;
    const key    = isPart ? 'spareParts' : addType === 'engines' ? 'engines' : 'vehicles';
    const list   = DB.get(key) || [];
    const prefix = isPart ? 'PRT' : addType === 'vehicles' ? 'VEH' : 'ENG';
    const id     = DB.genId(prefix);
    const sku    = `${prefix}-${(data.chassis || data.engineName || data.partName || 'ITEM').substring(0, 6).toUpperCase()}-${String(list.length + 1).padStart(3, '0')}`;
    const cutPtEntry = isCutVehicle ? (getPartType(normalizeCutType(data.cutType)) || resolvePartTypeEntry(data)) : null;
    const cutTypeName = cutPtEntry ? cutPtEntry.display : '';
    const newItem = isCutVehicle
      ? { ...data, id, sku, partName: `${data.make} ${data.model} — ${cutTypeName}`, partType: cutPtEntry.id, category: cutTypeName, vehicleClass: 'car', condition: 'Used', fitment: data.chassis || '', quantity: 1, daysInStock: 0, createdAt: new Date().toISOString().split('T')[0], created_by: user?.id || null, created_by_role: user?.role || null, created_by_name: user?.name || null }
      : { ...data, id, sku, daysInStock: 0, createdAt: new Date().toISOString().split('T')[0], created_by: user?.id || null, created_by_role: user?.role || null, created_by_name: user?.name || null };
    const result = await DB.setAsync(key, [...list, newItem]);
    if (result?.error) { console.error('[handleSave] Failed to save unit:', result.error); alert('Failed to save. Please try again.'); return; }
    autoGenerateQR(sku, key, id);
    setShowAdd(false);
    forceUpdate({});
  };

  const handleEditSave = async (data) => {
    const editTab = editItem?._tab || tab;
    const currentKey = editTab === 'vehicles' ? 'vehicles' : editTab === 'engines' ? 'engines' : 'spareParts';
    const updatedItem = { ...editItem, ...data, images: data.images, auction_sheets: data.auction_sheets || editItem.auction_sheets || [] };

    if (!data.partType && editItem.partType) updatedItem.partType = editItem.partType;

    // If editing a part and partType was set, sync category + vehicleClass
    if (currentKey === 'spareParts' && data.partType) {
      const pt = getPartType(data.partType);
      updatedItem.category = pt ? pt.display : (data.category || '');
      updatedItem.vehicleClass = data.vehicleClass || editItem.vehicleClass || 'car';
    }

    // If a vehicle now has a cutType, move it to spareParts
    if (currentKey === 'vehicles' && data.cutType) {
      const editPtEntry = getPartType(normalizeCutType(data.cutType)) || resolvePartTypeEntry(data);
      const cutTypeName = editPtEntry.display;
      const partItem = {
        ...updatedItem,
        partName: `${updatedItem.make || ''} ${updatedItem.model || ''} — ${cutTypeName}`.trim(),
        partType: editPtEntry.id, category: cutTypeName, vehicleClass: 'car',
        condition: updatedItem.condition || 'Used',
        fitment: updatedItem.chassis || '', quantity: updatedItem.quantity || 1,
      };
      const vList = DB.get('vehicles') || [];
      const vResult = await DB.setAsync('vehicles', vList.filter(i => i.id !== editItem.id));
      if (vResult?.error) { console.error('[handleEditSave] Failed to remove vehicle:', vResult.error); alert('Failed to save — vehicle removal failed. Please try again.'); return; }
      const pList = DB.get('spareParts') || [];
      const pResult = await DB.setAsync('spareParts', [...pList, partItem]);
      if (pResult?.error) { console.error('[handleEditSave] Failed to add part:', pResult.error); alert('Failed to save — part creation failed. Please try again.'); return; }
    } else {
      const list = DB.get(currentKey) || [];
      const result = await DB.setAsync(currentKey, list.map(i => i.id === editItem.id ? updatedItem : i));
      if (result?.error) { console.error('[handleEditSave] Failed to save:', result.error); alert('Failed to save changes. Please try again.'); return; }
    }
    setEditItem(null);
    forceUpdate({});
  };

  // --- Customer handlers ---
  const handleCustomerAdd = (item, cutType, customNote = '') => {
    if (cutType === 'full') {
      // Keep in vehicles as assigned
      const list = DB.get('vehicles') || [];
      DB.set('vehicles', list.map(i =>
        i.id === item.id ? { ...i, status: 'assigned', assignedTo: user.id, cutType } : i
      ));
    } else {
      // Remove from vehicles, move to spare parts
      DB.set('vehicles', (DB.get('vehicles') || []).filter(i => i.id !== item.id));
      const parts   = DB.get('spareParts') || [];
      const label   = cutType === 'custom' ? customNote : CUT_TYPES.find(c => c.id === cutType)?.name || cutType;
      const newPart = {
        id: DB.genId('PRT'),
        sku: `PRT-${(item.chassis || item.id).substring(0, 6).toUpperCase()}-${String(parts.length + 1).padStart(3, '0')}`,
        partName: `${getItemName(item)} — ${label}`,
        category: label,
        condition: 'Used',
        fitment: item.chassis || '',
        quantity: 1,
        port: item.port || '',
        status: 'assigned',
        assignedTo: user.id,
        images: item.images || [],
        costJPY: item.costJPY || 0,
        priceJPY: item.priceJPY || 0,
        daysInStock: item.daysInStock || 0,
        createdAt: new Date().toISOString().split('T')[0],
        cutType, customNote,
      };
      DB.set('spareParts', [...parts, newPart]);
    }
    logActivity.unitAssigned(getItemName(item), user.name, 'Customer Selection');
    setCutTypeModal(null);
    setSelectedCut(null);
    setCustomCutNote('');
    forceUpdate({});
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setCutTypeModal(null);
    setPageNum(1);
    setSelected(new Set());
    setSelectMode(false);
  };

  // ── Bulk select helpers ──
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === pagedItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pagedItems.map(i => i.id)));
    }
  };
  const bulkSetWebsite = (show) => {
    const key = tab === 'vehicles' ? 'vehicles' : tab === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(i => selected.has(i.id) ? { ...i, show_on_website: show, ...(show ? {} : { is_featured: false }) } : i));
    setSelected(new Set());
    setSelectMode(false);
    forceUpdate({});
  };
  const bulkSetFeatured = (feat) => {
    const key = tab === 'vehicles' ? 'vehicles' : tab === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(i => selected.has(i.id) ? { ...i, is_featured: feat, show_on_website: feat ? true : i.show_on_website } : i));
    setSelected(new Set());
    setSelectMode(false);
    forceUpdate({});
  };
  const bulkSetDestination = (yardId) => {
    if (!yardId) return;
    // Update across all tables since selected items could be from any tab
    ['vehicles', 'engines', 'spareParts'].forEach(key => {
      const list = DB.get(key) || [];
      const updated = list.map(i => selected.has(i.id) ? { ...i, destination_yard: yardId } : i);
      if (updated.some((item, idx) => item !== list[idx])) {
        DB.set(key, updated);
      }
    });
    setSelected(new Set());
    setSelectMode(false);
    forceUpdate({});
  };
  const bulkSetCountry = async (countryId) => {
    if (!countryId) return;
    for (const key of ['vehicles', 'engines', 'spareParts']) {
      const list = DB.get(key) || [];
      const updated = list.map(i => selected.has(i.id) ? { ...i, destination_country: countryId } : i);
      if (updated.some((item, idx) => item !== list[idx])) {
        const result = await DB.setAsync(key, updated);
        if (result?.error) { console.error('[bulkSetCountry] failed:', result.error); alert('Failed to assign country. Try again.'); return; }
      }
    }
    setSelected(new Set()); setSelectMode(false); forceUpdate({});
  };
  const bulkSetCutType = async (cutTypeId) => {
    if (!cutTypeId) return;
    for (const key of ['vehicles', 'engines', 'spareParts']) {
      const list = DB.get(key) || [];
      const updated = list.map(i => selected.has(i.id) ? { ...i, cut_type: cutTypeId } : i);
      if (updated.some((item, idx) => item !== list[idx])) {
        const result = await DB.setAsync(key, updated);
        if (result?.error) { console.error('[bulkSetCutType] failed:', result.error); alert('Failed to assign cut type. Try again.'); return; }
      }
    }
    setSelected(new Set()); setSelectMode(false); forceUpdate({});
  };

  const getItemIcon = (item) => {
    if (tab === 'vehicles' || item?._type === 'vehicle') return <CarIcon />;
    if (tab === 'engines'  || item?._type === 'engine')  return <EngineIcon />;
    return <PartsIcon />;
  };

  const getItemName = (item) =>
    item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || 'Unknown';

  const getItemSub = (item) => {
    const subParts = [];
    if (item.vehicleCategory && item.vehicleCategory !== 'passenger_car')
      subParts.push(item.vehicleCategory.replace(/_/g, ' '));
    if (item.extraSpecs?.truck_type) subParts.push(item.extraSpecs.truck_type.replace(/_/g, ' '));
    if (item.extraSpecs?.tonnage) subParts.push(item.extraSpecs.tonnage);
    if (item.extraSpecs?.bus_type) subParts.push(item.extraSpecs.bus_type.replace(/_/g, ' '));
    if (item.year)         subParts.push(item.year);
    if (item.chassis)      subParts.push(item.chassis);
    if (item.manufacturer) subParts.push(item.manufacturer);
    // Show actual cut type name instead of generic "Cut Unit"
    if (item.cutType) {
      const ctName = CUT_TYPES.find(c => c.id === item.cutType)?.name || item.cutType;
      subParts.push(ctName);
    } else if (item.category && item.category !== 'Cut Unit') {
      subParts.push(item.category);
    } else if (item.category === 'Cut Unit') {
      // Legacy data — try to extract cut type from partName
      const match = item.partName?.match(/— (.+)$/);
      subParts.push(match ? match[1] : 'Cut Unit');
    }
    if (item.transmission) subParts.push(item.transmission);
    return subParts.join(' • ');
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const safePage   = Math.min(pageNum, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * perPage, safePage * perPage);

  return (
    <div style={{ position: 'relative' }}>
      {/* ── Header: tabs + add button ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <div className="tabs">
          <button className={`tab ${tab === 'vehicles' ? 'active' : ''}`} onClick={() => handleTabChange('vehicles')}>
            {t('vehicles')}<span className="cnt">{baseVehicles.length}</span>
          </button>
          <button className={`tab ${tab === 'engines' ? 'active' : ''}`} onClick={() => handleTabChange('engines')}>
            {t('engines')}<span className="cnt">{baseEngines.length}</span>
          </button>
          <button className={`tab ${tab === 'parts' ? 'active' : ''}`} onClick={() => handleTabChange('parts')}>
            {t('parts')}<span className="cnt">{baseParts.length}</span>
          </button>
        </div>
        {role.canAdd && !isYardRole && (
          <button className="btn btn-primary" onClick={() => setShowChooser(true)}>
            <PlusIcon />{t('add')}
          </button>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className="inv-toolbar">
        <div className="inv-toolbar-left">
          {/* Search */}
          <div className="inv-search-wrap">
            <span className="inv-search-icon"><SearchIcon /></span>
            <input
              className="inv-search"
              placeholder="Search all inventory — make, model, SKU, chassis, part name…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPageNum(1); }}
            />
          </div>

          {/* Location filter: where the unit physically is (country) */}
          {!role.isCustomer && !isYardRole && (() => {
            const locations = [...new Set([...vehicles, ...engines, ...parts].map(u => u.country).filter(Boolean))].sort();
            if (locations.length === 0) return null;
            return (
              <select className={`inv-filter-select${filters.location ? ' active' : ''}`} value={filters.location} onChange={e => setFilter('location', e.target.value)}>
                <option value="">📍 All Locations</option>
                {locations.map(loc => <option key={loc} value={loc}>📍 {loc}</option>)}
              </select>
            );
          })()}

          {/* Destination Yard filter: where the unit is going */}
          {!role.isCustomer && !isYardRole && (
            <select className={`inv-filter-select${filters.yard ? ' active' : ''}`} value={filters.yard} onChange={e => setFilter('yard', e.target.value)}>
              <option value="">→ All Destinations</option>
              {getYardLocations().map(y => <option key={y.id} value={y.id}>→ {y.flag} {y.name}</option>)}
            </select>
          )}

          {/* Supplier filter (admin/head_admin only) */}
          {(user.role === 'admin' || user.role === 'head_admin') && (() => {
            const suppliers = [...new Set([...vehicles, ...engines, ...parts].map(u => u.purchase_supplier).filter(Boolean))].sort();
            if (suppliers.length === 0) return null;
            return (
              <select className={`inv-filter-select${filters.supplier ? ' active' : ''}`} value={filters.supplier} onChange={e => setFilter('supplier', e.target.value)}>
                <option value="">All Suppliers</option>
                {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            );
          })()}

          {/* Status filter — hidden for yard roles */}
          {!isYardRole && (
            <select
              className={`inv-filter-select${filters.status ? ' active' : ''}`}
              value={filters.status}
              onChange={e => setFilter('status', e.target.value)}
            >
              {role.isCustomer ? (
                <>
                  <option value="">{	("inv_all_items")}</option>
                  <option value="available">{t('status_available')}</option>
                  <option value="japan_yard">{t('status_japan_yard')}</option>
                  <option value="in_transit">{t('status_in_transit')}</option>
                  <option value="my_stock">{t('stock_title')}</option>
                </>
              ) : (
                <>
                  <option value="">{t('inv_all_status')}</option>
                  <option value="japan_yard">{t('status_japan_yard')}</option>
                  <option value="in_transit">{t('status_in_transit')}</option>
                  <option value="available">{t('status_available')}</option>
                  <option value="assigned">{t('status_assigned')}</option>
                  <option value="converted">Converted</option>
                </>
              )}
            </select>
          )}

          {/* Zone + Destination Country filters — yard roles only */}
          {isYardRole && (
            <>
              <select className={`inv-filter-select${filters.zone ? ' active' : ''}`} value={filters.zone} onChange={e => setFilter('zone', e.target.value)}>
                <option value="">All Zones</option>
                {getAllocatedZoneCodes(user.yard || 'hokkaido_yard').map(z => (
                  <option key={z} value={z}>{z === 'pool' ? 'Pool' : z.toUpperCase()}</option>
                ))}
              </select>
              <select className={`inv-filter-select${filters.destCountry ? ' active' : ''}`} value={filters.destCountry} onChange={e => setFilter('destCountry', e.target.value)}>
                <option value="">🌍 All Destinations</option>
                {getDestinationCountries().map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
              </select>
            </>
          )}

          {/* Vehicle-specific filters */}
          {tab === 'vehicles' && !isYardRole && (
            <>
              <select className={`inv-filter-select${filters.make ? ' active' : ''}`} value={filters.make} onChange={e => setFilter('make', e.target.value)}>
                <option value="">{t('inv_all_makes')}</option>
                {makes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select className={`inv-filter-select${filters.year ? ' active' : ''}`} value={filters.year} onChange={e => setFilter('year', e.target.value)}>
                <option value="">{t('inv_all_years')}</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className={`inv-filter-select${filters.transmission ? ' active' : ''}`} value={filters.transmission} onChange={e => setFilter('transmission', e.target.value)}>
                <option value="">{t('inv_all_transmissions')}</option>
                {transmissions.map(tr => <option key={tr} value={tr}>{tr}</option>)}
              </select>
            </>
          )}

          {/* Condition filter */}
          {(tab === 'engines' || tab === 'parts') && (
            <select className={`inv-filter-select${filters.condition ? ' active' : ''}`} value={filters.condition} onChange={e => setFilter('condition', e.target.value)}>
              <option value="">{t('inv_all_conditions')}</option>
              {conditions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}

          {/* Parts category */}
          {tab === 'parts' && (
            <select className={`inv-filter-select${filters.category ? ' active' : ''}`} value={filters.category} onChange={e => setFilter('category', e.target.value)}>
              <option value="">{t('inv_all_categories')}</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}

          {/* Customer filter */}
          {!role.isCustomer && !isYardRole && customerOptions.length > 0 && (
            <select
              className={`inv-filter-select${countryFilter ? ' active' : ''}`}
              value={countryFilter}
              onChange={e => setCountryFilter(e.target.value)}
            >
              <option value="">All Customers</option>
              {assignedCustomerIds.map(id => {
                const c = customerMap[id];
                if (!c) return null;
                return <option key={id} value={id}>{c.name}{c.country ? ` — ${c.country}` : ''}</option>;
              })}
            </select>
          )}

          {/* Sort */}
          <select className="inv-filter-select" value={filters.sort} onChange={e => setFilter('sort', e.target.value)}>
            {SORT_OPTIONS.filter(o => role.canSeeFinancials || !o.value.startsWith('price')).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <span className="inv-result-count">{filteredItems.length} of {allItems.length}</span>

          {hasActiveFilters && (
            <button className="inv-clear-btn" onClick={() => { setSearch(''); setFilters(EMPTY_FILTERS); setCountryFilter(''); setPageNum(1); }}>
              Clear
            </button>
          )}
        </div>

        <div className="inv-toolbar-right">
          {/* Bulk select toggle (staff, not yard/customer) */}
          {!role.isCustomer && user.role !== 'yard_operator' && user.role !== 'yard_manager' && (
            <button
              className={`btn btn-sm ${selectMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setSelectMode(m => !m); setSelected(new Set()); }}
              style={{ marginRight: 4 }}
            >
              <CheckIcon /> {selectMode ? 'Cancel' : 'Select'}
            </button>
          )}
          {selectMode && (
            <button className="btn btn-secondary btn-sm" onClick={selectAll} style={{ marginRight: 4 }}>
              {selected.size === pagedItems.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
          {!role.isCustomer && !isYardRole && (
            <div style={{ position: 'relative' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setDlOpen(o => !o)}>
                ⬇ Export ▾
              </button>
              {dlOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: 6, zIndex: 200, minWidth: 180,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', padding: '4px 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {tab === 'vehicles' ? 'Vehicles' : tab === 'engines' ? 'Engines' : 'Parts'}
                  </div>
                  <button className="dl-option-btn" onClick={() => {
                    const all = tab === 'vehicles' ? baseVehicles : tab === 'engines' ? baseEngines : baseParts;
                    exportCSV(all, `bigstar-${tab}-${new Date().toISOString().slice(0,10)}.csv`, { includeFinancials: role.canSeeFinancials });
                    setDlOpen(false);
                  }}>📊 Excel / CSV</button>
                  <button className="dl-option-btn" onClick={() => {
                    const all = tab === 'vehicles' ? baseVehicles : tab === 'engines' ? baseEngines : baseParts;
                    const label = tab === 'vehicles' ? 'Vehicles' : tab === 'engines' ? 'Engines' : 'Parts';
                    exportStockPDF(all, label);
                    setDlOpen(false);
                  }}>📄 PDF</button>
                </div>
              )}
            </div>
          )}
          <div className="view-toggle-wrap">
            <button className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`} onClick={() => setViewMode('grid')} title="Grid view"><GridIcon /></button>
            <button className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')} title="List view"><ListIcon /></button>
          </div>
        </div>
      </div>

      {/* ── Pagination (top) ── */}
      <Pagination total={filteredItems.length} page={safePage} perPage={perPage}
        onPageChange={setPageNum} onPerPageChange={n => { setPerPage(n); setPageNum(1); }} />

      {/* ── Empty state ── */}
      {filteredItems.length === 0 ? (
        <div className="empty">
          <PackageIcon />
          <div className="empty-title">
            {hasActiveFilters ? 'No items match your filters' : t('noData')}
          </div>
          {hasActiveFilters && (
            <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => { setSearch(''); setFilters(EMPTY_FILTERS); }}>
              Clear Filters
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (

        /* ── Grid view ── */
        <div className="inv-grid">
          {pagedItems.map(item => {
            const inMyStock   = role.isCustomer && item.assignedTo === user.id;
            const isVehicle   = tab === 'vehicles';
            const custOfferable = role.isCustomer ? canCustomerOffer(item, user) : true;

            return (
              <div
                key={item.id}
                className="inv-card"
                onClick={() => selectMode ? toggleSelect(item.id, { stopPropagation: () => {} }) : setDetail({ item, type: item._tab === 'engines' ? 'engines' : item._tab === 'parts' ? 'parts' : item._tab || tab })}
                style={{ cursor: 'pointer', outline: selected.has(item.id) ? '2px solid var(--blue)' : 'none', borderRadius: 12, opacity: (role.isCustomer && !custOfferable && !inMyStock) ? 0.5 : 1 }}
              >
                <div className="inv-card-img">
                  {/* Bulk select checkbox */}
                  {selectMode && (
                    <div onClick={e => toggleSelect(item.id, e)} style={{ position: 'absolute', top: 8, left: 8, zIndex: 5, width: 24, height: 24, borderRadius: 6, background: selected.has(item.id) ? 'var(--blue)' : 'rgba(0,0,0,0.5)', border: selected.has(item.id) ? 'none' : '2px solid rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      {selected.has(item.id) && <CheckIcon />}
                    </div>
                  )}
                  {/* Website/Featured indicators */}
                  {!selectMode && item.show_on_website && (
                    <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 4, display: 'flex', gap: 3 }}>
                      <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.85)', color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>WEB</span>
                      {item.is_featured && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(234,179,8,0.9)', color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' }}>★</span>}
                    </div>
                  )}
                  {item.images?.length ? <img src={item.images[0]} alt="" /> : getItemIcon(item)}
                  {inMyStock ? (
                    <span className="inv-badge available" style={{ background: 'var(--green)', color: '#fff' }}>✓ My Stock</span>
                  ) : (
                    <span className={`inv-badge ${item.status}`}>{t('status_' + item.status) || item.status}</span>
                  )}
                  <span className="inv-days">{item.daysInStock ?? 0}d</span>
                  {item.quantity > 1 && (
                    <span style={{ position: 'absolute', top: 8, right: 8, padding: '2px 7px', borderRadius: 4, background: 'rgba(139,92,246,0.9)', color: '#fff', fontSize: 10, fontWeight: 700, zIndex: 4 }}>×{item.quantity - (item.sold_quantity || 0)}</span>
                  )}
                </div>

                <div className="inv-card-body">
                  <div className="inv-card-title">
                    {getItemName(item)}
                    {/* Quantity badge for bulk items */}
                    {item.quantity > 1 && (
                      <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 4, background: 'rgba(6,182,212,0.15)', color: 'var(--cyan)', fontSize: 10, fontWeight: 700, verticalAlign: 'middle' }}>x{item.quantity}</span>
                    )}
                    {/* Category/type badge */}
                    {tab === 'parts' && item.category && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'var(--bg3)', color: 'var(--text3)', fontSize: 9, fontWeight: 600, verticalAlign: 'middle' }}>{
                        (item.category === 'Cut Types' || item.category === 'Cut Unit')
                          ? (CUT_TYPES.find(c => c.id === item.cutType)?.name || item.partName?.split('—').pop()?.trim() || item.category)
                          : item.category
                      }</span>
                    )}
                    {tab === 'vehicles' && item.cutType && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#8B5CF6', fontSize: 9, fontWeight: 600, verticalAlign: 'middle' }}>{CUT_TYPES.find(c => c.id === item.cutType)?.name || item.cutType}</span>
                    )}
                  </div>
                  <div className="inv-card-sku">{item.sku}</div>
                  <div className="inv-card-sub">{getItemSub(item)}</div>
                  <div className="inv-card-footer">
                    <div style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📍 {[item.city, item.country].filter(Boolean).join(', ') || item.location || item.port || '—'}
                    </div>
                    <span className={`status status-${STATUS_CLASS[item.status] || item.status}`} style={{ fontSize: 10 }}>
                      {t('status_' + item.status) || item.status}
                    </span>
                  </div>
                  {(() => {
                    const yard = item.destination_yard ? getYardLocations().find(y => y.id === item.destination_yard) : null;
                    return yard ? (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        → {yard.flag} {yard.name}
                      </div>
                    ) : null;
                  })()}

                  {!role.isCustomer && item.destination_country && (() => {
                    const dc = getDestinationCountries().find(c => c.id === item.destination_country);
                    return dc ? (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Destination: {dc.flag} {dc.name}
                      </div>
                    ) : null;
                  })()}

                  {/* Assigned customer label (staff view — hidden from yard_sales) */}
                  {role.canSeeFinancials && item.assignedTo && (
                    <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      → {customers.find(c => c.id === item.assignedTo)?.name || 'Customer'}
                    </div>
                  )}

                  {/* Yard sales: show bid count instead of price */}
                  {isYardSales && (() => {
                    const bids = (DB.get('yardBids') || []).filter(b => b.unit_id === item.id && b.status === 'active');
                    const highest = bids.length > 0 ? bids.reduce((max, b) => (b.amount || 0) > (max.amount || 0) ? b : max, bids[0]) : null;
                    return (
                      <div style={{ marginTop: 6, fontSize: 11 }}>
                        {bids.length > 0 ? (
                          <span>📊 {bids.length} bid{bids.length !== 1 ? 's' : ''} · 🏆 {highest.currency} {highest.amount?.toLocaleString()}</span>
                        ) : (
                          <span style={{ color: 'var(--text3)' }}>No bids yet</span>
                        )}
                      </div>
                    );
                  })()}

                  {/* Customer: Add to My Stock (via customer_stock table) */}
                  {role.isCustomer && (() => {
                    const stock = DB.get('customerStock') || [];
                    const isInMyStock = stock.some(s => s.customerId === user.id && s.unitId === item.id);

                    if (!custOfferable) {
                      if (isInMyStock) return <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', textAlign: 'center', fontWeight: 600, opacity: 0.6 }}>✓ In My Stock</div>;
                      if (item.destination_country) return <div style={{ marginTop: 8, fontSize: 10, color: 'var(--red)', textAlign: 'center', fontWeight: 600 }}>Reserved for another market</div>;
                      return <div style={{ marginTop: 8, fontSize: 11, color: 'var(--red)', textAlign: 'center', fontWeight: 600 }}>{t('status_sold')}</div>;
                    }

                    if (isInMyStock) return <div style={{ marginTop: 8, fontSize: 11, color: 'var(--green)', textAlign: 'center', fontWeight: 600 }}>✓ In My Stock</div>;

                    const addToStock = (e) => {
                      e.stopPropagation();
                      const unitType = item._type === 'vehicle' ? 'vehicle' : item._type === 'engine' ? 'engine' : 'spare_part';
                      DB.set('customerStock', [...stock, { id: DB.genId('CS'), customerId: user.id, unitId: item.id, unitType, addedAt: new Date().toISOString() }]);
                      forceUpdate({});
                    };

                    const isCuttable = isVehicle || (item.category && CUTTABLE_CATS.has(item.category)) || item.cutType;
                    const partsLabel = isVehicle ? t('inv_request_parts') : item.category ? `Parts from ${item.category}` : t('inv_request_parts');

                    if (isCuttable) return (
                      <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                        <button className="inv-add-btn" style={{ flex: 1 }} onClick={e => { addToStock(e); }}>{t('inv_add_to_stock')}</button>
                        <button className="inv-add-btn" style={{ flex: 1, background: 'var(--bg3)', color: 'var(--blue)', border: '1px solid var(--blue)' }}
                          onClick={e => { e.stopPropagation(); setPartsReqVehicle(item); }}>
                          {partsLabel}
                        </button>
                      </div>
                    );
                    return <button className="inv-add-btn" onClick={addToStock}>{t('inv_add_to_stock')}</button>;
                  })()}

                  {/* Staff edit + download + delete buttons */}
                  {role.canAdd && !isYardRole && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: 8, width: '100%' }}
                      onClick={e => { e.stopPropagation(); setEditItem(item); }}
                    >
                      <EditIcon /> {t('edit')}
                    </button>
                  )}
                  {!role.isCustomer && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: 6, width: '100%' }}
                      onClick={e => { e.stopPropagation(); downloadUnitPDF(item); }}
                    >
                      ⬇ {t('download')}
                    </button>
                  )}
                  {role.canDelete && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: 6, color: 'var(--red)', borderColor: 'var(--red)', width: '100%' }}
                      onClick={e => handleDelete(item, e)}
                    >
                      <DeleteIcon /> {t('delete')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      ) : (

        /* ── List view ── */
        <div className="inv-list">
          <div className="inv-list-header">
            {role.isCustomer && tab !== 'vehicles' && <div style={{ width: 28, flexShrink: 0 }} />}
            <div style={{ width: 52, flexShrink: 0 }} />
            <div className="inv-list-info">{t('inv_item')}</div>
            <div className="inv-list-col">{t('inv_details')}</div>
            <div className="inv-list-col">Status</div>
            {!role.isCustomer && <div className="inv-list-col price">{t('detail_price')}</div>}
            <div className="inv-list-col days">Days</div>
            {/* Spacers matching row buttons so columns align */}
            {role.isCustomer && tab === 'vehicles' && <div style={{ width: 52, flexShrink: 0 }} />}
            {role.canAdd && <div style={{ width: 40, flexShrink: 0 }} />}
            {role.canDelete && <div style={{ width: 40, flexShrink: 0 }} />}
          </div>

          {pagedItems.map(item => {
            const isVehicle  = tab === 'vehicles';

            return (
              <div
                key={item.id}
                className="inv-list-row"
                onClick={() => selectMode ? toggleSelect(item.id, { stopPropagation: () => {} }) : setDetail({ item, type: item._tab === 'engines' ? 'engines' : item._tab === 'parts' ? 'parts' : item._tab || tab })}
                style={{ outline: selected.has(item.id) ? '2px solid var(--blue)' : 'none', borderRadius: 8 }}
              >
                {/* Bulk select checkbox (list view) */}
                {selectMode && (
                  <div onClick={e => toggleSelect(item.id, e)} style={{ width: 24, height: 24, borderRadius: 6, background: selected.has(item.id) ? 'var(--blue)' : 'var(--bg3)', border: selected.has(item.id) ? 'none' : '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginRight: 4 }}>
                    {selected.has(item.id) && <CheckIcon />}
                  </div>
                )}
                <div className="inv-list-img">
                  {item.images?.length ? <img src={item.images[0]} alt="" /> : getItemIcon(item)}
                </div>
                <div className="inv-list-info">
                  <div className="inv-list-name">
                    {getItemName(item)}
                    {item.quantity > 1 && (
                      <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: 'rgba(6,182,212,0.15)', color: 'var(--cyan)', fontSize: 9, fontWeight: 700, verticalAlign: 'middle' }}>x{item.quantity}</span>
                    )}
                  </div>
                  <div className="inv-list-sku">{item.sku}</div>
                </div>
                <div className="inv-list-col">{getItemSub(item) || '—'}</div>
                <div className="inv-list-col">
                  <span className={`status status-${STATUS_CLASS[item.status] || item.status}`} style={{ fontSize: 10 }}>{t('status_' + item.status) || item.status}</span>
                  {item.country && <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>📍 {item.country}</div>}
                  {(() => {
                    const yard = item.destination_yard ? getYardLocations().find(y => y.id === item.destination_yard) : null;
                    return yard ? <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 1 }}>→ {yard.flag} {yard.name}</div> : null;
                  })()}
                </div>
                {role.canSeeFinancials && <div className="inv-list-col price">{formatPrice(item.priceJPY, currency)}</div>}
                <div className="inv-list-col days">{item.daysInStock ?? 0}d</div>
                {/* Website/Featured badges — separate from status */}
                {!selectMode && item.show_on_website && (
                  <div style={{ display: 'flex', gap: 2, flexShrink: 0, minWidth: 36 }}>
                    <span style={{ padding: '2px 5px', borderRadius: 3, background: 'rgba(37,99,235,0.15)', color: 'var(--blue)', fontSize: 8, fontWeight: 700 }}>🌐</span>
                    {item.is_featured && <span style={{ padding: '2px 5px', borderRadius: 3, background: 'rgba(234,179,8,0.15)', color: '#EAB308', fontSize: 8, fontWeight: 700 }}>★</span>}
                  </div>
                )}

                {/* Customer: Add to My Stock (list view, via customer_stock) */}
                {role.isCustomer && (() => {
                  const stock = DB.get('customerStock') || [];
                  const inMyStock = stock.some(s => s.customerId === user.id && s.unitId === item.id);
                  if (item.status === 'sold') return <span style={{ fontSize: 10, color: 'var(--red)', fontWeight: 600, flexShrink: 0 }}>{t('status_sold')}</span>;
                  if (inMyStock) return <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600, flexShrink: 0 }}>✓ Added</span>;
                  return <button className="inv-add-btn-sm" onClick={e => {
                    e.stopPropagation();
                    const unitType = item._type === 'vehicle' ? 'vehicle' : item._type === 'engine' ? 'engine' : 'spare_part';
                    DB.set('customerStock', [...stock, { id: DB.genId('CS'), customerId: user.id, unitId: item.id, unitType, addedAt: new Date().toISOString() }]);
                    forceUpdate({});
                  }}>{t('btn_add')}</button>;
                })()}

                {/* Staff edit button (list view) */}
                {role.canAdd && !isYardRole && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); setEditItem(item); }}
                  >
                    <EditIcon />
                  </button>
                )}

                {/* Admin delete button (list view) */}
                {role.canDelete && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ color: 'var(--red)', borderColor: 'var(--red)', flexShrink: 0 }}
                    onClick={e => handleDelete(item, e)}
                  >
                    <DeleteIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Bulk action bar (floating) ── */}
      {selectMode && selected.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 100,
          background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 14,
          padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', minWidth: 320,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginRight: 4 }}>
            {selected.size} selected
          </span>
          <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
          <button className="btn btn-sm" onClick={() => bulkSetWebsite(true)}
            style={{ background: '#2563EB', color: '#fff', border: 'none', fontSize: 12, padding: '6px 14px', borderRadius: 8 }}>
            🌐 Add to Website
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetWebsite(false)}
            style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', fontSize: 12, padding: '6px 14px', borderRadius: 8 }}>
            Remove from Website
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetFeatured(true)}
            style={{ background: '#EAB308', color: '#fff', border: 'none', fontSize: 12, padding: '6px 14px', borderRadius: 8 }}>
            ★ Set Featured
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetFeatured(false)}
            style={{ background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', fontSize: 12, padding: '6px 14px', borderRadius: 8 }}>
            Unfeature
          </button>
          <div style={{ width: 1, height: 24, background: 'var(--border)' }} />
          <select
            onChange={e => { if (e.target.value) bulkSetDestination(e.target.value); }}
            style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', fontSize: 12, padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
            defaultValue=""
          >
            <option value="" disabled>→ Set Destination</option>
            {getYardLocations().map(y => <option key={y.id} value={y.id}>{y.flag} {y.name}</option>)}
          </select>
          {(user.role === 'admin' || user.role === 'head_admin') && (
            <>
              <select
                onChange={e => { if (e.target.value) bulkSetCountry(e.target.value); }}
                style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', fontSize: 12, padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                defaultValue=""
              >
                <option value="" disabled>🌍 Set Country</option>
                {getDestinationCountries().map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
              </select>
              <select
                onChange={e => { if (e.target.value) bulkSetCutType(e.target.value); }}
                style={{ background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)', fontSize: 12, padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                defaultValue=""
              >
                <option value="" disabled>✂ Set Cut Type</option>
                <option value="cbu">CBU</option>
                <option value="half_cut">Half Cut</option>
                <option value="ckd">CKD</option>
              </select>
            </>
          )}
        </div>
      )}

      {/* ── Pagination (bottom) ── */}
      <Pagination total={filteredItems.length} page={safePage} perPage={perPage}
        onPageChange={setPageNum} onPerPageChange={n => { setPerPage(n); setPageNum(1); }} />

      {/* ── Detail Modal ── */}
      {detail && (
        <DetailModal
          item={detail.item}
          type={detail.type}
          onClose={() => setDetail(null)}
          currency={currency}
          canPrice={role.canPrice}
          isCustomer={role.isCustomer}
          lang={lang}
          onUpdate={handleUpdate}
          user={user}
          onRequestParts={role.isCustomer ? (v) => { setDetail(null); setPartsReqVehicle(v); } : undefined}
        />
      )}

      {/* ── Add Unit Chooser (staff) ── */}
      {showChooser && (
        <AddUnitChooser
          onClose={() => { setShowChooser(false); forceUpdate({}); }}
          onSave={(choice) => {
            setShowChooser(false);
            if (choice === 'full_car' || choice === 'parts') setShowAdd(choice);
          }}
          canPrice={role.canPrice}
          lang={lang}
          user={user}
        />
      )}

      {/* ── Add Modal — Full Car or Parts (staff) ── */}
      {showAdd && (
        <AddModal
          type={showAdd === 'parts' ? 'parts' : tab === 'engines' ? 'engines' : 'vehicles'}
          onSave={handleSave}
          onClose={() => setShowAdd(false)}
          canPrice={role.canPrice}
          lang={lang}
          user={user}
        />
      )}

      {/* ── Edit Modal (staff) ── */}
      {editItem && (
        <AddModal
          type={tab}
          initialData={editItem}
          onSave={handleEditSave}
          onClose={() => setEditItem(null)}
          canPrice={role.canPrice}
          lang={lang}
          user={user}
        />
      )}

      {/* ── Cut Type Picker Modal (customer, vehicles) ── */}
      {cutTypeModal && (
        <div className="modal-overlay" onClick={() => setCutTypeModal(null)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">{t('inv_how_want_vehicle')}</h3>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
                  {getItemName(cutTypeModal)}
                  {cutTypeModal.year && ` · ${cutTypeModal.year}`}
                  {cutTypeModal.sku && <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', marginLeft: 8 }}>{cutTypeModal.sku}</span>}
                </div>
              </div>
              <button className="modal-close" onClick={() => setCutTypeModal(null)}>×</button>
            </div>

            <div className="modal-body">
              {/* Vehicle image or icon */}
              {cutTypeModal.images?.length > 0 && (
                <div className="cut-modal-img">
                  <img src={cutTypeModal.images[0]} alt={getItemName(cutTypeModal)} />
                </div>
              )}

              <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 16 }}>
                Select how you would like to receive this vehicle. This will be noted on your order.
              </p>

              <div className="cut-type-grid">
                {/* Full vehicle — keeps unit in vehicles section */}
                <button
                  className={`cut-type-option${selectedCut === 'full' ? ' selected' : ''}`}
                  onClick={() => { setSelectedCut('full'); setCustomCutNote(''); }}
                >
                  <span className="cut-type-icon">🚗</span>
                  <span className="cut-type-name">{t('inv_full_vehicle')}</span>
                  <span className="cut-type-desc">{t('inv_full_vehicle_desc')}</span>
                </button>
                {CUT_TYPES.map(ct => {
                  const meta = CUT_TYPE_META[ct.id] || { icon: '📦', desc: '' };
                  return (
                    <button
                      key={ct.id}
                      className={`cut-type-option${selectedCut === ct.id ? ' selected' : ''}`}
                      onClick={() => { setSelectedCut(ct.id); setCustomCutNote(''); }}
                    >
                      <span className="cut-type-icon">{meta.icon}</span>
                      <span className="cut-type-name">{ct.name}</span>
                      <span className="cut-type-desc">{meta.desc}</span>
                    </button>
                  );
                })}
                {/* Custom cut option */}
                <button
                  className={`cut-type-option${selectedCut === 'custom' ? ' selected' : ''}`}
                  onClick={() => setSelectedCut('custom')}
                >
                  <span className="cut-type-icon">✏️</span>
                  <span className="cut-type-name">{t('inv_custom')}</span>
                  <span className="cut-type-desc">{t('inv_custom_desc')}</span>
                </button>
              </div>

              {/* Custom note input */}
              {selectedCut === 'custom' && (
                <div style={{ marginTop: 14 }}>
                  <textarea
                    className="form-input"
                    rows={3}
                    placeholder="Describe exactly what you need (e.g. front half with engine, dashboard removed)..."
                    value={customCutNote}
                    onChange={e => setCustomCutNote(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                    autoFocus
                  />
                </div>
              )}

              {/* Confirm button */}
              {selectedCut && (
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: 16 }}
                  disabled={selectedCut === 'custom' && !customCutNote.trim()}
                  onClick={() => handleCustomerAdd(cutTypeModal, selectedCut, customCutNote)}
                >
                  Confirm Selection
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Parts Request Modal (customer, vehicles) ── */}
      {partsReqVehicle && (
        <PartsRequestModal
          vehicle={partsReqVehicle}
          user={user}
          onClose={() => setPartsReqVehicle(null)}
          onSave={() => forceUpdate({})}
        />
      )}
    </div>
  );
}

export default Inventory;
